import React, { useCallback, useEffect, useContext, useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Loader } from "ui/Loader";
import { archiveConversation, fetchArchivedConversations, fetchThread, showOrMaskConversation, threadKeys, useConfirmThreadOffer, useRefuseThreadOffer, useThreadOffers } from "hooks/thread";
import { createBlockUser, deleteBlockUser } from "hooks/blockUser";
import { createReportUser } from "hooks/reportUser";
import { useInterlocutorPresence } from "hooks/presence";
import DiscussionTitle from "./DiscussionTitle";
import Actions from "./Actions";
import InputSendMessage from "./InputSendMessage";
import DiscussionContent from "./DiscussionContent";
import ConfirmModal from "ui/Modal/ConfirmModal";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import type { ChatMessage, ChatSocket, MessageUser, RenderItem } from "../types";
import { getErrorResponseData, hasKey, isRecord } from "helpers/typing";
import ConfirmModalCustomContent from "ui/Modal/ConfirmModalCustomContent";
import Select from "ui/Form/Select";
import { useReferences } from "hooks/reference";
import { buildRoute, ROUTES } from "routes";
import {
    adKeys,
    useOurAd,
    useOurAdApplications,
    useAdApplications,
    useConfirmAdApply,
    useRefuseAdApply,
} from "hooks/ad";
import { AdApplyStatus, type AdApplicationInterface } from "types/ad";
import OfferPanel from "./OfferPanel";
import { Sheet, SheetContent } from "components/ui/sheet";
import { Clock3, MapPin, Shield, Zap } from "lucide-react";
import { toast } from "sonner";

type Props = {
    socket: ChatSocket;
    refreshDataConversation: () => void;
};

const Discussion: React.FC<Props> = ({ socket, refreshDataConversation }) => {
    const { service_id: serviceId } = useParams() as { service_id: string };
    const { client_id: clientId } = useParams() as { client_id: string };
    const concatServiceClientId = serviceId + clientId;
    const auth = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const reference = useReferences();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const headerMenuRef = useRef<HTMLDivElement>(null);

    const [messagesReceived, setMessagesReceived] = useState<ChatMessage[]>([]);
    const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
    const [showReportModal, setShowReportModal] = useState<boolean>(false);
    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
    const [showUnarchiveModal, setShowUnarchiveModal] = useState<boolean>(false);
    const [hasReport, setHasReport] = useState<boolean>(false);
    const [reportingCode, setReportingCode] = useState<string>();
    const adId = searchParams.get("ad") ?? undefined;
    const offerRequested = searchParams.get("offer") === "1";
    const [isMobileOfferSheet, setIsMobileOfferSheet] = useState(false);
    const { data: offerAd } = useOurAd(adId);

    const hasNotifiedReadReceiptRef = useRef(false);
    const { data, isLoading, refetch, isSuccess, isFetchedAfterMount } = useQuery({
        queryKey: ["thread", serviceId, clientId],
        queryFn: () =>
            fetchThread({
                serviceId,
                clientId,
            }),
        enabled: !!serviceId && !!clientId,
        staleTime: 0,
        refetchOnMount: "always",
    });

    const isInterlocutorProvider = useMemo(() => {
        if (!data?.service) {
            return false;
        }
        return auth.account?.user.id !== data.service.provider.user_id;
    }, [auth.account?.user.id, data?.service]);

    const interlocutorUserId = useMemo(() => {
        if (!data?.service) {
            return undefined;
        }
        if (isInterlocutorProvider) {
            return data.service.provider.user_id;
        }
        return data.client.id;
    }, [data?.service, data?.client.id, isInterlocutorProvider]);

    const threadAdId = data?.service?.ad_id ?? undefined;
    // Ad context: from the ad flow URL (?ad=) or from the backend once an application/offer exists.
    const discussionAdId = adId ?? threadAdId;
    const isDirectConversation = !discussionAdId;

    const confirmThreadOffer = useConfirmThreadOffer();
    const refuseThreadOffer = useRefuseThreadOffer();
    const { data: threadOffers } = useThreadOffers(serviceId, clientId, isDirectConversation);

    // Provider: fetch own applications ; Client: fetch applications for the ad
    const { data: ourApplications } = useOurAdApplications(!isInterlocutorProvider);
    const { data: adApplications } = useAdApplications(isInterlocutorProvider ? discussionAdId : undefined);

    const confirmAdApply = useConfirmAdApply();
    const refuseAdApply = useRefuseAdApply();

    const isApplicationBookingCancelled = useCallback(
        (app: AdApplicationInterface) =>
            Boolean(app.booking?.cancelled_at ?? app.ad?.booking?.cancelled_at) ||
            app.status === AdApplyStatus.CANCELLED,
        [],
    );

    const allApplications = React.useMemo(() => {
        if (isDirectConversation) {
            const applications = threadOffers as AdApplicationInterface[] | undefined;
            if (!applications) return [];
            return applications.filter(
                (app) =>
                    app.service_id === serviceId &&
                    app.client_id === clientId &&
                    (app.status === AdApplyStatus.PENDING ||
                        app.status === AdApplyStatus.ACCEPTED ||
                        app.status === AdApplyStatus.HIRED ||
                        app.status === AdApplyStatus.REFUSED ||
                        app.status === AdApplyStatus.EXPIRED ||
                        app.status === AdApplyStatus.CANCELLED),
            );
        }

        const applications = (isInterlocutorProvider ? adApplications : ourApplications) as
            | AdApplicationInterface[]
            | undefined;
        if (!applications) return [];
        return applications.filter(
            (app) =>
                app.service_id === serviceId &&
                app.ad_id === discussionAdId &&
                (app.status === AdApplyStatus.PENDING ||
                    app.status === AdApplyStatus.ACCEPTED ||
                    app.status === AdApplyStatus.HIRED ||
                    app.status === AdApplyStatus.REFUSED ||
                    app.status === AdApplyStatus.EXPIRED ||
                    app.status === AdApplyStatus.CANCELLED),
        );
    }, [
        threadOffers,
        isDirectConversation,
        ourApplications,
        adApplications,
        isInterlocutorProvider,
        serviceId,
        clientId,
        discussionAdId,
    ]);

    // Most recent application — used for accept/refuse actions
    const confirmedApplication = allApplications.at(-1) ?? null;

    const hasActiveBooking = useMemo(
        () =>
            allApplications.some(
                (app) =>
                    !isApplicationBookingCancelled(app) &&
                    (app.status === AdApplyStatus.PENDING ||
                        app.status === AdApplyStatus.ACCEPTED ||
                        app.status === AdApplyStatus.HIRED),
            ),
        [allApplications, isApplicationBookingCancelled],
    );

    const notifyConversationUpdate = useCallback(() => {
        socket.emit("send_message", { room: concatServiceClientId });
        if (interlocutorUserId) {
            socket.emit("refresh_threads_with_new_message", {
                user_id: interlocutorUserId,
            });
        }
    }, [socket, concatServiceClientId, interlocutorUserId]);

    const handleAccept = useCallback(
        (options: { activate_cesu_plus: string; cesu_plus_status: boolean; aici_status: boolean }) => {
            if (!confirmedApplication) return;

            const onSuccess = async () => {
                notifyConversationUpdate();
                if (isDirectConversation) {
                    await queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
                } else {
                    await queryClient.invalidateQueries({ queryKey: adKeys.applications() });
                    await queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
                }
            };

            if (isDirectConversation) {
                void confirmThreadOffer
                    .mutateAsync({
                        serviceId,
                        clientId,
                        data: {
                            service_id: confirmedApplication.service_id,
                            activate_cesu_plus: options.activate_cesu_plus,
                            cesu_plus_status: options.cesu_plus_status ? "1" : "0",
                            aici_status: options.aici_status ? "1" : "0",
                        },
                    })
                    .then(() => void onSuccess());
                return;
            }

            void confirmAdApply
                .mutateAsync({
                    adId: confirmedApplication.ad_id!,
                    data: {
                        service_id: confirmedApplication.service_id,
                        activate_cesu_plus: options.activate_cesu_plus,
                        cesu_plus_status: options.cesu_plus_status ? "1" : "0",
                        aici_status: options.aici_status ? "1" : "0",
                    },
                })
                .then(() => void onSuccess());
        },
        [
            confirmedApplication,
            confirmAdApply,
            confirmThreadOffer,
            notifyConversationUpdate,
            queryClient,
            isDirectConversation,
            serviceId,
            clientId,
        ],
    );

    const handleRefuse = useCallback(() => {
        if (!confirmedApplication) return;

        const onSuccess = () => {
            notifyConversationUpdate();
        };

        if (isDirectConversation) {
            void refuseThreadOffer
                .mutateAsync({
                    serviceId,
                    clientId,
                    data: { service_id: confirmedApplication.service_id },
                })
                .then(onSuccess);
            return;
        }

        void refuseAdApply
            .mutateAsync({
                adId: confirmedApplication.ad_id!,
                data: { service_id: confirmedApplication.service_id },
            })
            .then(onSuccess);
    }, [
        confirmedApplication,
        refuseAdApply,
        refuseThreadOffer,
        notifyConversationUpdate,
        isDirectConversation,
        serviceId,
        clientId,
    ]);

    const renderItems = useMemo<RenderItem[]>(() => {
        const items: RenderItem[] = messagesReceived.map((msg) => ({ kind: "message", data: msg }));
        for (const app of allApplications) {
            const isLatest = app === confirmedApplication;
            items.push({
                kind: "offer_card",
                data: app,
                isEmployerView: isInterlocutorProvider,
                onAccept: isLatest && isInterlocutorProvider ? handleAccept : undefined,
                onRefuse: isLatest && isInterlocutorProvider ? handleRefuse : undefined,
            });
        }
        return items.sort((a, b) => new Date(a.data.created_at).getTime() - new Date(b.data.created_at).getTime());
    }, [messagesReceived, allApplications, confirmedApplication, isInterlocutorProvider, handleAccept, handleRefuse]);

    const refreshData = useCallback(() => {
        void refetch();
    }, [refetch]);

    const { data: archivedConversations, refetch: refetchArchived } = useQuery({
        queryKey: ["archived-conversations"],
        queryFn: fetchArchivedConversations,
        staleTime: 30_000,
    });

    const isArchived = useMemo(
        () => archivedConversations?.some((c) => c.service_id === serviceId && c.client_id === clientId) ?? false,
        [archivedConversations, serviceId, clientId],
    );

    const checkIdentityOfUser: MessageUser | undefined = useMemo(() => {
        if (!data || !data.service || !data.client || !serviceId || !clientId) {
            return;
        }

        if (auth.account?.user.id !== data.service.provider.user_id) {
            return {
                ...data.service.provider,
                user_id: data.service.provider.user_id,
                dataleon_status: data.service.provider.dataleon_status,
            };
        }

        return {
            id: data.client.id,
            user_id: data.client.id,
            first_name: data.client.first_name,
            last_name: data.client.last_name,
            picture_id: data.client.picture_id,
            dataleon_status: data.client.dataleon_status,
        };
    }, [auth.account?.user.id, data, serviceId, clientId]);

    const isOnline = useInterlocutorPresence(socket, interlocutorUserId);

    const closeHeaderMenu = () => setIsHeaderMenuOpen(false);

    const handleShowConfirmModalReport = () => {
        setShowReportModal(true);
        closeHeaderMenu();
        setHasReport(true);
    };

    const handleOnClickShowInterlocutorProfile = () => {
        if (isInterlocutorProvider && serviceId) {
            navigate(buildRoute.provider(serviceId));
        }
        closeHeaderMenu();
    };

    const handleOnClickShowAd = () => {
        if (discussionAdId) navigate(buildRoute.ad(discussionAdId));
    };

    const handleShowArchiveModal = () => {
        setShowArchiveModal(true);
        closeHeaderMenu();
    };

    const handleShowUnarchiveModal = () => {
        setShowUnarchiveModal(true);
        closeHeaderMenu();
    };

    const handleConfirmUnarchive = async () => {
        const archivedConversation = archivedConversations?.find(
            (c) => c.service_id === serviceId && c.client_id === clientId,
        );
        const messageId = archivedConversation?.id;
        if (!messageId) {
            setShowUnarchiveModal(false);
            return;
        }
        try {
            await showOrMaskConversation(messageId, { show_or_mask: "show" });
            setShowUnarchiveModal(false);
            refreshDataConversation();
            void refetchArchived();
            await queryClient.refetchQueries({ queryKey: ["all-conversations"] });
        } catch {
            setShowUnarchiveModal(false);
            toast.error(intl.formatMessage("booking.messages.error.unarchive_failed"));
        }
    };

    const handleConfirmArchive = async () => {
        if (!serviceId || !clientId) {
            toast.error(intl.formatMessage("booking.messages.error.archive_failed"));
            return;
        }

        const messageId =
            data?.threads?.find((message) => message.id)?.id ?? messagesReceived.find((message) => message.id)?.id;

        try {
            await archiveConversation(serviceId, clientId, messageId);
            setShowArchiveModal(false);
            await queryClient.refetchQueries({ queryKey: ["all-conversations"] });
            await queryClient.invalidateQueries({ queryKey: ["archived-conversations"] });
            refreshDataConversation();
            navigate(ROUTES.MESSAGING);
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                formatErrorResponse(errData);
            }
            toast.error(intl.formatMessage("booking.messages.error.archive_failed"));
        }
    };

    const handleConfirmModalBlock = async () => {
        setShowBlockModal(false);
        if (!data || !checkIdentityOfUser) {
            return;
        }
        if (data.blockUser) {
            await deleteBlockUser(checkIdentityOfUser.user_id ?? checkIdentityOfUser.id ?? "");
        } else {
            await createBlockUser({
                blocked_user_id: checkIdentityOfUser.user_id ?? checkIdentityOfUser.id ?? "",
            });
        }
        refreshData();
        setHasReport(false);
    };

    const handleConfirmModalReport = async () => {
        setShowReportModal(false);
        if (!data || !checkIdentityOfUser) {
            return;
        }

        try {
            await createReportUser({
                service_id: serviceId,
                report_user_id: checkIdentityOfUser.user_id ?? checkIdentityOfUser.id ?? "",
                reporting_code: reportingCode,
            });

            if (!data.blockUser) {
                setShowBlockModal(true);
            }

            refreshData();
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                if (
                    isRecord(errData.data) &&
                    errData.data.statusCode === 400 &&
                    hasKey(errData.data, "data") &&
                    isRecord(errData.data.data) &&
                    hasKey(errData.data.data, "report_user_id") &&
                    isRecord(errData.data.data.report_user_id) &&
                    errData.data.data.report_user_id.message ===
                        intl.formatMessage("booking.error.report.cant_report_user_multiple_time_less_than_month")
                ) {
                    toast.error(intl.formatMessage("booking.error.report.cant_report_user_multiple_time_less_than_month"));
                }
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    useEffect(() => {
        if (data?.threads) {
            setMessagesReceived(data.threads);
        }
    }, [data, setMessagesReceived]);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 1023px)");
        const updateOfferLayout = (event: MediaQueryList | MediaQueryListEvent) => {
            setIsMobileOfferSheet(event.matches);
        };

        updateOfferLayout(mediaQuery);
        mediaQuery.addEventListener("change", updateOfferLayout);

        return () => mediaQuery.removeEventListener("change", updateOfferLayout);
    }, []);

    useEffect(() => {
        hasNotifiedReadReceiptRef.current = false;
    }, [serviceId, clientId]);

    useEffect(() => {
        socket.emit("join_room", concatServiceClientId);
    }, [concatServiceClientId, socket]);

    useEffect(() => {
        if (!isSuccess || !isFetchedAfterMount || hasNotifiedReadReceiptRef.current) {
            return;
        }

        // GET thread marks the conversation as read; refresh badges and notify the sender.
        hasNotifiedReadReceiptRef.current = true;
        refreshDataConversation();
        socket.emit("send_message", { room: concatServiceClientId });
    }, [isSuccess, isFetchedAfterMount, refreshDataConversation, socket, concatServiceClientId]);

    useEffect(() => {
        socket.on("update_messages", () => {
            refreshData();
            refreshDataConversation();
            void queryClient.invalidateQueries({ queryKey: adKeys.applications() });
            void queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
            void queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
        });

        return () => {
            socket.off("update_messages");
        };
    }, [socket, refreshData, refreshDataConversation, queryClient]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target instanceof Node ? event.target : null;
            if (headerMenuRef.current && target && !headerMenuRef.current.contains(target)) {
                setIsHeaderMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!data) {
        return <Loader />;
    }

    const closeOffer = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("offer");
        setSearchParams(next, { replace: true });
    };

    const handleApplied = async () => {
        toast.success(intl.formatMessage("booking.messages.offer.success"));
        closeOffer();
        const refreshedThread = await refetch();

        if (refreshedThread.data?.threads) {
            setMessagesReceived(refreshedThread.data.threads);
        }

        refreshDataConversation();
        await queryClient.refetchQueries({ queryKey: ["all-conversations"] });
        await queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
        notifyConversationUpdate();
    };

    const serviceCategory = data.service?.category
        ? data.service.subcategory
            ? intl.formatMessage(
                  `subcategory.${data.service.category.toString().toLowerCase()}.${data.service.subcategory.toString().toLowerCase()}.label`,
              )
            : intl.formatMessage(`service.label.category.${data.service.category.toString()}`)
        : null;

    const providerCity = data.service?.provider?.city ?? null;
    const canShowOfferPanel = offerRequested && Boolean(data.service) && (Boolean(offerAd) || isDirectConversation);
    const canShowOfferButton = !isInterlocutorProvider && !hasActiveBooking && (Boolean(discussionAdId) || isDirectConversation);

    return (
        <React.Fragment>
            <div className="flex h-full overflow-hidden">
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                    <div className="sticky top-0 z-20 bg-white flex-shrink-0">
                        <div className="relative" ref={headerMenuRef}>
                            <DiscussionTitle
                                checkIdentityOfUser={checkIdentityOfUser ?? null}
                                data={data}
                                isInterlocutorProvider={isInterlocutorProvider}
                                isOnline={isOnline}
                                isHeaderMenuOpen={isHeaderMenuOpen}
                                onToggleHeaderMenu={() => setIsHeaderMenuOpen((open) => !open)}
                            />
                            {isHeaderMenuOpen && (
                                <div className="absolute right-4 top-full z-30">
                                    <Actions
                                        onShowProfile={handleOnClickShowInterlocutorProfile}
                                        onArchive={isArchived ? handleShowUnarchiveModal : handleShowArchiveModal}
                                        onBlockOrReport={handleShowConfirmModalReport}
                                        isArchived={isArchived}
                                        showProfile={isInterlocutorProvider}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {serviceCategory && discussionAdId && (
                        <div className="flex items-center justify-between gap-4 px-6 pt-4 pb-px bg-gradient-to-r from-[#eef2ff] to-[#faf5ff] border-b border-[#e0e7ff] flex-shrink-0">
                            <div className="flex-1 min-w-0 pb-4">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-[11px] font-black leading-[16.5px] uppercase tracking-[1.1px] text-[#4f39f6]">
                                        {intl.formatMessage("booking.messages.label.application_for_ad")}
                                    </span>
                                    {data.service && !data.service.is_active && (
                                        <span className="text-[11px] font-black uppercase tracking-[1.1px] text-[#4f39f6] opacity-60">
                                            {intl.formatMessage("booking.messages.label.expired")}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleOnClickShowAd}
                                    className="text-[15px] font-black leading-[22.5px] text-[#1a1c2d] hover:underline truncate text-left block max-w-full mb-1"
                                >
                                    {serviceCategory}
                                </button>
                                <div className="flex items-center gap-4 flex-wrap">
                                    {providerCity && (
                                        <span className="flex items-center gap-1 text-[12px] font-medium text-[#45556c]">
                                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                            {providerCity}
                                        </span>
                                    )}
                                    {data.service?.frequency && (
                                        <span className="flex items-center gap-1 text-[12px] font-medium text-[#45556c]">
                                            <Clock3 className="w-3.5 h-3.5 flex-shrink-0" />
                                            {data.service.frequency === "ONE_TIME"
                                                ? intl.formatMessage("ad.card.frequency_one_time")
                                                : intl.formatMessage("ad.card.frequency_recurring")}
                                        </span>
                                    )}
                                    {data.service?.price && (
                                        <span className="text-[12px] font-medium text-[#45556c]">
                                            {data.service.price}€{intl.formatMessage("provider.card.price_per_hour")}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleOnClickShowAd}
                                className="flex-shrink-0 self-start mt-3 text-[13px] font-black text-[#4c4cf4] bg-white border border-[#e0e7ff] hover:border-[#c7d2fe] hover:bg-[#f5f3ff] transition-colors px-4 py-2.5 rounded-[14px] whitespace-nowrap"
                            >
                                {intl.formatMessage("ad.label.view")}
                            </button>
                        </div>
                    )}

                    <div className="flex items-center gap-[12px] px-6 py-2 bg-[#fffbeb] border-b border-[#fef3c6] flex-shrink-0">
                        <Shield className="w-4 h-4 text-[#7b3306] flex-shrink-0" />
                        <p className="text-[12px] leading-[18px] text-[#7b3306]">
                            <span className="font-black">
                                {intl.formatMessage("booking.messages.label.security_warning_title")}
                            </span>
                            <span className="font-medium">
                                {intl.formatMessage("booking.messages.label.security_warning_body")}
                            </span>
                        </p>
                    </div>

                    <div className="relative flex-1 overflow-hidden flex flex-col">
                        <DiscussionContent checkIdentityOfUser={checkIdentityOfUser} renderItems={renderItems} />
                        {!isInterlocutorProvider && canShowOfferButton && (
                            <button
                                type="button"
                                onClick={() => {
                                    const next = new URLSearchParams(searchParams);
                                    next.set("offer", "1");
                                    if (discussionAdId) {
                                        next.set("ad", discussionAdId);
                                    }
                                    setSearchParams(next, { replace: true });
                                }}
                                className="absolute bottom-4 right-4 flex items-center gap-2 bg-[#00bc7d] hover:bg-[#00a36d] active:bg-[#009960] text-white font-black text-[14px] px-5 py-3 rounded-full shadow-lg transition-colors z-10"
                            >
                                <Zap className="size-4 fill-white" />
                                {intl.formatMessage("booking.messages.offer.cta_button")}
                            </button>
                        )}
                    </div>

                    <div className="flex-shrink-0">
                        {data.blockUser?.blocked_user_id ? (
                            <div className="px-4 py-4 bg-white border-t border-border">
                                <p className="text-sm text-muted-foreground text-center">
                                    {intl.formatMessage("booking.messages.label.cannot_send_message")}
                                </p>
                            </div>
                        ) : (
                            <InputSendMessage
                                socket={socket}
                                messagesReceived={messagesReceived}
                                setMessagesReceived={setMessagesReceived}
                                refreshDataConversation={refreshDataConversation}
                                refreshDataActiveConversation={refreshData}
                                interlocutorUserId={interlocutorUserId}
                            />
                        )}
                    </div>
                </div>
                <aside
                    className="hidden lg:flex flex-shrink-0 border-l border-slate-100 bg-white shadow-[-5px_0px_30px_0px_rgba(0,0,0,0.08)] overflow-hidden"
                    style={{
                        width: offerRequested && !isMobileOfferSheet ? 520 : 0,
                        transition: "width 300ms ease-in-out",
                    }}
                >
                    <div className="w-[520px] flex-shrink-0 h-full">
                        {data.service && (offerAd || isDirectConversation) && (
                            <OfferPanel
                                ad={offerAd}
                                clientId={clientId}
                                service={data.service}
                                onApplied={handleApplied}
                                onClose={closeOffer}
                            />
                        )}
                    </div>
                </aside>
            </div>

            {isMobileOfferSheet && (
                <Sheet open={canShowOfferPanel} onOpenChange={(open) => !open && closeOffer()}>
                    <SheetContent side="right" className="w-full overscroll-contain p-0 sm:max-w-md [&>button]:hidden">
                        {data.service && (offerAd || isDirectConversation) && (
                            <OfferPanel
                                ad={offerAd}
                                clientId={clientId}
                                service={data.service}
                                onApplied={handleApplied}
                                onClose={closeOffer}
                            />
                        )}
                    </SheetContent>
                </Sheet>
            )}

            <ConfirmModal
                show={showArchiveModal}
                onClose={() => setShowArchiveModal(false)}
                onConfirm={() => {
                    void handleConfirmArchive();
                }}
                title={intl.formatMessage("booking.messages.modal.archive.label.title")}
                message={intl.formatMessage("booking.messages.modal.archive.label.message")}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
            />

            <ConfirmModal
                show={showUnarchiveModal}
                onClose={() => setShowUnarchiveModal(false)}
                onConfirm={() => {
                    void handleConfirmUnarchive();
                }}
                title={intl.formatMessage("booking.messages.modal.unarchive.label.title")}
                message={intl.formatMessage("booking.messages.modal.unarchive.label.message")}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
            />

            <ConfirmModal
                show={showBlockModal}
                onClose={() => setShowBlockModal(false)}
                onConfirm={handleConfirmModalBlock}
                title={intl.formatMessage(
                    "booking.messages.modal." +
                        (data.blockUser && data.blockUser?.blocked_user_id !== auth.account?.user.id
                            ? "unblock"
                            : "block") +
                        ".label.title",
                    {
                        first_name: checkIdentityOfUser?.first_name ?? "",
                        last_name: checkIdentityOfUser?.last_name ?? "",
                    },
                )}
                message={intl.formatMessage(
                    "booking.messages.modal." +
                        (hasReport
                            ? "block_report"
                            : data.blockUser && data.blockUser?.blocked_user_id !== auth.account?.user.id
                              ? "unblock"
                              : "block") +
                        ".label.message",
                )}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
            />

            <ConfirmModalCustomContent
                show={showReportModal}
                onClose={() => {
                    setShowReportModal(false);
                    setHasReport(false);
                }}
                onConfirm={handleConfirmModalReport}
                title={intl.formatMessage("booking.messages.modal.report.label.title")}
                message={intl.formatMessage("booking.messages.modal.report.label.message")}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
            >
                <Select
                    inputProps={{
                        name: "reporting",
                        placeholder: intl.formatMessage("booking.messages.modal.report.label.reason_reporting"),
                        options: reference.selectList("reporting").map((reporting) => {
                            return {
                                label: reporting.label.toString(),
                                value: reporting.value.toString(),
                            };
                        }),
                        onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
                            setReportingCode(event.target.value);
                        },
                    }}
                    labelProps={{
                        label: intl.formatMessage("booking.messages.modal.report.label.reason_reporting"),
                    }}
                />
            </ConfirmModalCustomContent>
        </React.Fragment>
    );
};

export default React.memo(Discussion);
